import Spinner from '@amzn/awsui-components-core/spinner';
import React from 'react';
import MemberSubsetGrid from 'src/components/star-grid/components/subset-editor/MemberSubsetGrid';
import type { MemberSubsetGridProps } from 'src/components/star-grid/components/subset-editor/MemberSubsetGrid';
import styles from './styles/MemberSubsetModal.module.scss';

interface Props {
  /** Root→member labels of the shallowest ticked position. */
  readonly chain: readonly string[];
  readonly loading: boolean;
  readonly viewLoading: boolean;
  readonly aliasLabelsLoading: boolean;
  readonly isSearching: boolean;
  readonly hasMembers: boolean;
  /** Remounts the grid on a view action, which is the only moment it reads its expansion seed. */
  readonly gridKey: string;
  readonly grid: Omit<MemberSubsetGridProps, 'testId'>;
  readonly testId?: string;
}

export const MemberSubsetMemberList: React.FC<Props> = ({
  chain,
  loading,
  viewLoading,
  aliasLabelsLoading,
  isSearching,
  hasMembers,
  gridKey,
  grid,
  testId
}) => (
  <>
    {chain.length > 1 && (
      <div className={styles.chain} data-testid={testId ? `${testId}-chain` : undefined}>
        {chain.map((label, depth) => (
          <span key={`${depth}:${label}`}>
            {depth > 0 && <span aria-hidden> › </span>}
            {label}
          </span>
        ))}
      </div>
    )}
    <div className={styles.memberList} data-testid={testId ? `${testId}-list` : undefined}>
      {(loading && grid.rows.length === 0) || viewLoading ? (
        <div className={styles.emptyState}>
          <Spinner /> {viewLoading ? 'Loading view…' : aliasLabelsLoading ? 'Applying alias…' : 'Loading members…'}
        </div>
      ) : grid.rows.length === 0 && (isSearching || !hasMembers) ? (
        // An emptied grid (Remove All) keeps its header with no rows, so only these two get a placeholder.
        <div className={styles.emptyState}>{isSearching ? 'No members match your search.' : 'This hierarchy has no members.'}</div>
      ) : (
        <>
          <MemberSubsetGrid key={gridKey} {...grid} testId={testId ? `${testId}-grid` : undefined} />
          {/* An overlay, not a replacement: unmounting the grid would lose every expand since the last view action. */}
          {aliasLabelsLoading && (
            <div className={styles.aliasOverlay}>
              <Spinner /> Applying alias…
            </div>
          )}
        </>
      )}
    </div>
  </>
);
